import { isNonNull } from '@fullstory/typeutils';
import { useMemo } from 'react';

type TooltipDataPoint = {
  /**
   * The index of the point's item in the dataset.
   */
  datasetItemIndex: number;
};

/**
 * Sorts the dataset, ordering items with tooltip data points last, so they appear stacked
 * on top on charts.
 *
 * If an activePoint is also supplied, it will get the top-most treatment.
 */
export function useOrderedDataset<T>(
  dataset: T[],
  tooltipData: TooltipDataPoint[] | null,
  activePoint?: TooltipDataPoint | null,
) {
  return useMemo((): T[] => {
    if (!tooltipData || !tooltipData.length) return dataset;

    const activePointDatasetItemIndex = activePoint?.datasetItemIndex;

    const tooltipDataIndexes = tooltipData
      .map(d => d.datasetItemIndex)
      .filter(i => i !== activePointDatasetItemIndex);
    const uniqueTooltipDataIndexes = new Set(tooltipDataIndexes);

    const activePointDatum =
      activePointDatasetItemIndex !== undefined ? dataset[activePointDatasetItemIndex] : undefined;

    return [
      // dataset items sans tooltip data
      ...dataset.filter(
        (_, i) => !uniqueTooltipDataIndexes.has(i) && i !== activePointDatasetItemIndex,
      ),
      // tooltip data, sans active point
      ...Array.from(uniqueTooltipDataIndexes)
        .sort()
        .map(index => dataset[index])
        .filter(isNonNull),
      // active point
      ...(activePointDatum !== undefined ? [activePointDatum] : []),
    ];
  }, [activePoint, dataset, tooltipData]);
}
